import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  MessageSquare, Inbox, Loader2, User
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/messages?page_size=200`);
      setMessages(response.data.data ?? response.data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API_URL}/api/student/messages/${messageId}/read`);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
      setSelectedMessage(prev =>
        prev?.id === messageId ? { ...prev, read: true } : prev
      );
    } catch (err) {
      console.error('Failed to mark message as read:', err);
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read && message.receiver_id === user?.id) {
      markAsRead(message.id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-GB', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
  };

  const getMessageTypeLabel = (type) => {
    switch (type) {
      case 'activity': return 'Activity';
      case 'grade': return 'Grade';
      case 'announcement': return 'Announcement';
      default: return 'Message';
    }
  };

  const getMessageTypeColor = (type) => {
    switch (type) {
      case 'activity': return 'bg-[#F5A623]/10 text-[#F5A623]';
      case 'grade': return 'bg-[#4CAF50]/10 text-[#4CAF50]';
      case 'announcement': return 'bg-[#D72B2B]/10 text-[#D72B2B]';
      default: return 'bg-[#1A2E6E]/10 text-[#1A2E6E]';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-messages-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
          Messages
        </h1>
        <p className="text-[#1A2E6E]/60 mt-1">
          View messages from your teacher
        </p>
      </div>

      {/* Messages Layout */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden min-h-[500px]">
        <div className="flex h-[500px]">
          {/* Message List */}
          <div className={`
            w-full lg:w-96 border-r border-[#E8EAF0] overflow-y-auto
            ${selectedMessage ? 'hidden lg:block' : 'block'}
          `}>
            {messages.length > 0 ? (
              <div className="divide-y divide-[#E8EAF0]">
                {messages.map((message, index) => {
                  const isReceived = message.receiver_id === user?.id;
                  return (
                    <motion.button
                      key={message.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => handleSelectMessage(message)}
                      className={`
                        w-full p-4 text-left hover:bg-[#FAFAF7] transition-colors
                        ${selectedMessage?.id === message.id ? 'bg-[#FAFAF7]' : ''}
                        ${!message.read && isReceived ? 'bg-[#D72B2B]/5' : ''}
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-[#1A2E6E]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-medium text-[#1A2E6E] truncate">
                              {isReceived ? 'Teacher' : 'You'}
                            </span>
                            <span className="text-xs text-[#1A2E6E]/50 shrink-0">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                          {message.subject && (
                            <p className="text-sm font-medium text-[#1A2E6E] truncate mb-1">
                              {message.subject}
                            </p>
                          )}
                          <p className="text-sm text-[#1A2E6E]/60 truncate">
                            {message.content}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`
                              text-xs px-2 py-0.5 rounded-full
                              ${getMessageTypeColor(message.message_type)}
                            `}>
                              {getMessageTypeLabel(message.message_type)}
                            </span>
                            {!message.read && isReceived && (
                              <span className="w-2 h-2 bg-[#D72B2B] rounded-full" />
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <Inbox className="w-16 h-16 text-[#1A2E6E]/20 mb-4" />
                <p className="text-[#1A2E6E]/60 text-center">
                  No messages yet
                </p>
              </div>
            )}
          </div>

          {/* Message Detail */}
          <div className={`
            flex-1 flex flex-col
            ${selectedMessage ? 'block' : 'hidden lg:flex'}
          `}>
            {selectedMessage ? (
              <>
                {/* Mobile back button */}
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="lg:hidden p-4 border-b border-[#E8EAF0] text-sm text-[#1A2E6E] hover:bg-[#FAFAF7]"
                >
                  {'< Back to messages'}
                </button>

                {/* Message Header */}
                <div className="p-6 border-b border-[#E8EAF0]">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-[#1A2E6E]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-[#1A2E6E]">
                          {selectedMessage.receiver_id === user?.id ? 'Teacher' : 'You'}
                        </span>
                        <span className={`
                          text-xs px-2 py-0.5 rounded-full
                          ${getMessageTypeColor(selectedMessage.message_type)}
                        `}>
                          {getMessageTypeLabel(selectedMessage.message_type)}
                        </span>
                      </div>
                      <p className="text-sm text-[#1A2E6E]/60">
                        {new Date(selectedMessage.created_at).toLocaleString('en-GB')}
                      </p>
                    </div>
                  </div>
                  {selectedMessage.subject && (
                    <h2 className="mt-4 text-xl font-semibold text-[#1A2E6E]">
                      {selectedMessage.subject}
                    </h2>
                  )}
                </div>

                {/* Message Content */}
                <div className="flex-1 p-6 overflow-y-auto">
                  <p className="text-[#1A2E6E]/80 whitespace-pre-wrap leading-relaxed">
                    {selectedMessage.content}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <MessageSquare className="w-16 h-16 text-[#1A2E6E]/20 mb-4" />
                <p className="text-[#1A2E6E]/60 text-center">
                  Select a message to view its content
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentMessages;
